import { useT } from "@transifex/react";
import { CoverImage } from "p6-react-shared";
import { useState } from "react";
import LoadingSpinner from "../../../basic/loadingSpinner/LoadingSpinner";
import UserContentCover from "../../../basic/userContentCover/UserContentCover";
import { useBookContext } from "../../../context/BookContext";
import { getDateTimeFromMilliseconds } from "../../../helpers/DateTime";

import {
    DisabledWhenEditingSpan,
    RecentBooksMainWrapper,
    RecentlyUsedBook,
    RecentlyUsedBookCoverWrapper,
    RecentlyUsedBookDate,
    RecentlyUsedBookInfo,
    RecentlyUsedBooksSpinnerWrapper,
    RecentlyUsedBooksWrapper,
    RecentlyUsedBookTitle,
    SourceOptionArrowWrapper,
    SourceOptionIconWrapper,
    SourceOptionItem,
    SourceOptionItemTitle,
    SourceOptionItemTitleWrapper,
    StyledArrowIcon,
    StyledP6Icon,
} from "./StyledComponents";

export interface RecentlyUsedContentProps {
    onSelectRecent: (contentId: string, isOwnSubject: boolean) => void;
    isDisabled?: boolean;
    disabledLabel?: string;
}

const RecentlyUsedContent = (props: RecentlyUsedContentProps) => {
    const { recentlyUsedBooks, recentBookSubjects, blockedBookSubjects, areRecentBooksLoading } = useBookContext();

    const t = useT();
    const t_recentBooks = t("Recently used Content", { _tags: "CreateTest,RecentlyUsedContent" });
    const t_lastUsed = t("Last used:", { _tags: "CreateTest,RecentlyUsedContent" });

    const [hideRecentBooks, setHideRecentBooks] = useState(true);

    // Filtered here rather than on load because the blocked list depends on the flow. Books with
    // no resolved subject are kept.
    const recentBooks = Object.values(recentlyUsedBooks).filter((book) => {
        const subjectId = book.articleId ? recentBookSubjects[book.articleId] : undefined;
        return !subjectId || !blockedBookSubjects.includes(subjectId);
    });
    const hasBooksToDisplay = recentBooks.filter((b) => !!b.articleId || !!b.ownSubjectId).length > 0;

    const renderRecentBooksHeader = () => {
        return (
            <SourceOptionItem
                data-testid="source-option-recent"
                onClick={() => {
                    setHideRecentBooks((val) => !val);
                }}
                className={!hideRecentBooks ? "orange" : ""}
                noBorder
            >
                <SourceOptionIconWrapper>
                    <StyledP6Icon name={"clock"} />
                </SourceOptionIconWrapper>
                <SourceOptionItemTitleWrapper>
                    <SourceOptionItemTitle>
                        {t_recentBooks}
                        {props.isDisabled && props.disabledLabel && (
                            <>
                                <br />
                                <DisabledWhenEditingSpan>{props.disabledLabel}</DisabledWhenEditingSpan>
                            </>
                        )}
                    </SourceOptionItemTitle>
                </SourceOptionItemTitleWrapper>
                <SourceOptionArrowWrapper>
                    <StyledArrowIcon
                        onClick={() => {}}
                        direction={hideRecentBooks ? "RIGHT" : "BOTTOM"}
                    />
                </SourceOptionArrowWrapper>
            </SourceOptionItem>
        );
    };

    const renderRecentBooksContent = () => {
        return (
            <RecentlyUsedBooksWrapper amountRecent={recentBooks.length}>
                {recentBooks
                    .sort((a, b) => (b.date || 0) - (a.date || 0))
                    .map((book) => {
                        if (!book.articleId && !book.ownSubjectId) {
                            return false;
                        }
                        return (
                            <RecentlyUsedBook
                                data-testid={`recent-content-item-${book.articleId || book.ownSubjectId}`}
                                amountRecent={recentBooks.length}
                                key={book.articleId || book.ownSubjectId}
                                onClick={() => {
                                    props.onSelectRecent((book.articleId || book.ownSubjectId)!, !!book.ownSubjectId);
                                }}
                            >
                                <RecentlyUsedBookCoverWrapper>
                                    {book.imagePath && (
                                        <CoverImage
                                            imageId={"https://www.phase-6.de" + book.imagePath}
                                            size={"small"}
                                        />
                                    )}
                                    {book.primaryLang && (
                                        <UserContentCover
                                            primaryLanguage={book.primaryLang}
                                            secondaryLanguage={book.secondaryLang || ""}
                                            small
                                        />
                                    )}
                                </RecentlyUsedBookCoverWrapper>
                                <RecentlyUsedBookInfo>
                                    <RecentlyUsedBookTitle>{book.name}</RecentlyUsedBookTitle>
                                    <RecentlyUsedBookDate>
                                        {t_lastUsed}
                                        <br />
                                        {book.date && getDateTimeFromMilliseconds(book.date)}
                                    </RecentlyUsedBookDate>
                                </RecentlyUsedBookInfo>
                            </RecentlyUsedBook>
                        );
                    })}
            </RecentlyUsedBooksWrapper>
        );
    };

    const renderSpinner = () => {
        return (
            <RecentlyUsedBooksSpinnerWrapper>
                <LoadingSpinner size={"h-l w-l"} />
            </RecentlyUsedBooksSpinnerWrapper>
        );
    };

    // Mounted while loading so the header keeps its place instead of appearing once the books
    // resolve. The tradeoff: a logged-in teacher with no recents yet sees the header disappear when
    // loading ends, and the spinner is only visible to someone who expands the section mid-load.
    // Anonymous users never load recents at all, so they never see either.
    if (!hasBooksToDisplay && !areRecentBooksLoading) {
        return null;
    }

    return (
        <RecentBooksMainWrapper className={`recentBooks ${props.isDisabled ? "disabled" : ""}`}>
            {renderRecentBooksHeader()}
            {!hideRecentBooks && (areRecentBooksLoading ? renderSpinner() : renderRecentBooksContent())}
        </RecentBooksMainWrapper>
    );
};

export default RecentlyUsedContent;
